import * as z from "zod";

export enum OrderImportField {
  // Order info
  ORDER_NUMBER = "orderNumber",
  ORDER_DATE = "orderDate",
  DATE_PAID = "datePaid",
  // Customer / buyer
  BUYER_EMAIL = "buyerEmail",
  BUYER_PHONE = "buyerPhone",
  // Recipient
  RECIPIENT_FULL_NAME = "recipientFullName",
  RECIPIENT_FIRST_NAME = "recipientFirstName",
  RECIPIENT_LAST_NAME = "recipientLastName",
  RECIPIENT_COMPANY = "recipientCompany",
  // Address
  ADDRESS_LINE_1 = "addressLine1",
  ADDRESS_LINE_2 = "addressLine2",
  CITY = "city",
  STATE = "state",
  POSTAL_CODE = "postalCode",
  COUNTRY_CODE = "countryCode",
  // Per-item (line item fields)
  ITEM_SKU = "itemSku",
  ITEM_NAME = "itemName",
  ITEM_QUANTITY = "itemQuantity",
  ITEM_UNIT_PRICE = "itemUnitPrice",
  ITEM_WEIGHT_OZ = "itemWeightOz",
  // Order-level package
  ORDER_WEIGHT_LBS = "orderWeightLbs",
  ORDER_DIMENSIONS = "orderDimensions",
  // Notes
  INTERNAL_NOTES = "internalNotes",
  NOTES_TO_BUYER = "notesToBuyer",
  GIFT_MESSAGE = "giftMessage",
}

export enum ImportRowFormat {
  // One CSV row per line item; rows are grouped by ORDER_NUMBER column
  MULTIPLE_ROWS_PER_ORDER = "multipleRowsPerOrder",
  // One CSV row per order; item fields use a "{n}" placeholder template
  // e.g. "Product SKU's {n}", "Product Quantity {n}"
  SINGLE_ROW_WITH_NUMBERED_COLUMNS = "singleRowWithNumberedColumns",
}

// Per-item fields that use numbered templates in SINGLE_ROW_WITH_NUMBERED_COLUMNS format
export const ITEM_LEVEL_FIELDS = new Set<OrderImportField>([
  OrderImportField.ITEM_SKU,
  OrderImportField.ITEM_NAME,
  OrderImportField.ITEM_QUANTITY,
  OrderImportField.ITEM_UNIT_PRICE,
  OrderImportField.ITEM_WEIGHT_OZ,
]);

const orderImportColumnMapping_ = z
  .object({
    rowFormat: z.enum(ImportRowFormat),
    fieldMappings: z.record(z.enum(OrderImportField), z.string().optional()),
  })
  .superRefine(({ fieldMappings, rowFormat }, ctx) => {
    if (rowFormat !== ImportRowFormat.MULTIPLE_ROWS_PER_ORDER) {
      return;
    }

    const requiredFields = [
      [OrderImportField.ORDER_NUMBER, "order number"],
      [OrderImportField.ITEM_SKU, "SKU"],
      [OrderImportField.ITEM_QUANTITY, "quantity"],
    ] as const;

    for (const [field, label] of requiredFields) {
      if (!fieldMappings[field]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Multiple rows per order requires a ${label} column. Use single row per order for files with numbered item columns.`,
          path: ["fieldMappings", field],
        });
      }
    }
  });

export const OrderImportColumnMappingSchema: z.ZodType<OrderImportColumnMapping> =
  orderImportColumnMapping_;
export interface OrderImportColumnMapping {
  rowFormat: ImportRowFormat;
  fieldMappings: Partial<Record<OrderImportField, string>>;
}

const customOrderImportSchema_ = z.object({
  id: z.string(),
  teamId: z.string(),
  name: z.string(),
  columnMapping: OrderImportColumnMappingSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CustomOrderImportSchemaSchema: z.ZodType<CustomOrderImportSchema> =
  customOrderImportSchema_;
export interface CustomOrderImportSchema extends z.infer<
  typeof customOrderImportSchema_
> {}
